import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Table,
    Button,
    Space,
    Input,
    Card,
    Drawer,
    Tag,
    notification,
    Popconfirm,
    Row,
    Col,
    Statistic,
    Tooltip,
    Avatar,
    Select,
    InputNumber,
    Divider,
    Typography
} from 'antd';

import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    ShoppingCartOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DollarCircleOutlined,
    UserOutlined,
    EyeOutlined,
    FileTextOutlined,
    BellOutlined
} from '@ant-design/icons';
import { orderService } from '../../services/orderService';
import { customerService } from '../../services/customerService';
import OrderForm from './OrderForm';
import ProformaGenerator from './ProformaGenerator';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const { Column } = Table;
const { Search } = Input;
const { Option } = Select;

const OrderList = ({ dateRange }) => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [searchText, setSearchText] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        district: '',
        kw: ''
    });
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [stats, setStats] = useState({
        counts: { Total: 0, Open: 0, InProgress: 0, Completed: 0, Closed: 0 },
        revenue: 0
    });
    console.log("Order Stats:", stats); // Using the variable to suppress unused warning
    const [customers, setCustomers] = useState([]);
    const [isProformaVisible, setIsProformaVisible] = useState(false);
    const [isAlertsDrawerVisible, setIsAlertsDrawerVisible] = useState(false);
    const [allAlerts, setAllAlerts] = useState([]);

    const fetchOrders = async (page = 1, search = '', currentFilters = filters) => {
        try {
            setLoading(true);
            const response = await orderService.getAllOrders({
                page,
                limit: pagination.pageSize,
                search,
                status: currentFilters.status,
                district: currentFilters.district,
                kw: currentFilters.kw,
                startDate: dateRange?.startDate,
                endDate: dateRange?.endDate
            });

            setOrders(Array.isArray(response?.data) ? response.data : []);
            if (response?.stats) {
                setStats(response.stats);
            }

            setPagination(prev => ({
                ...prev,
                current: response?.pagination?.page || 1,
                total: response?.pagination?.total || 0
            }));

            // Fetch all alerts from backend (not just current page)
            fetchAlerts();
        } catch (err) {
            console.error(err);
            notification.error({
                message: 'Error',
                description: 'Failed to fetch orders'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(1, searchText);
        fetchCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange]);

    const fetchAlerts = async () => {
        try {
            const response = await orderService.getOrderAlerts();
            if (response.success) {
                setAllAlerts(response.data || []);
            }
        } catch (err) {
            console.error("Failed to fetch order alerts", err);
        }
    };

    const fetchCustomers = async () => {
        try {
            const res = await customerService.getAllCustomers();
            setCustomers(res.data || []);
        } catch (err) {
            console.error("Failed to fetch customers", err);
        }
    };

    useEffect(() => {
        const editId = searchParams.get('edit');
        if (editId) {
            const fetchFullOrder = async () => {
                try {
                    setLoading(true);
                    const res = await orderService.getOrderById(editId);
                    if (res.success) {
                        setSelectedOrder(res.data);
                        setIsDrawerVisible(true);
                    }
                } catch (err) {
                    notification.error({ message: 'Error', description: 'Failed to load order for editing' });
                } finally {
                    setLoading(false);
                }
            };
            fetchFullOrder();
        }
    }, [searchParams]);

    const handleTableChange = (pagination) => {
        fetchOrders(pagination.current, searchText);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        fetchOrders(1, value);
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        fetchOrders(1, searchText, newFilters);
    };

    // Removed local calculateAlerts in favor of backend fetchAlerts

    const handleAdd = () => {
        setSelectedOrder(null);
        setIsDrawerVisible(true);
    };

    const handleEdit = (order) => {
        setSearchParams({ edit: order.id });
    };

    const handleDelete = async (id) => {
        try {
            await orderService.deleteOrder(id);
            notification.success({
                message: 'Success',
                description: 'Order deleted successfully'
            });
            fetchOrders(pagination.current, searchText);
        } catch (err) {
            console.error(err);
            notification.error({
                message: 'Error',
                description: 'Failed to delete order'
            });
        }
    };

    const statusColors = {
        Open: 'blue',
        InProgress: 'orange',
        Completed: 'green',
        Closed: 'default'
    };



    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>

            <Card
                title="Orders Management"
                extra={
                    <Space>
                        <Button
                            icon={<BellOutlined />}
                            onClick={() => setIsAlertsDrawerVisible(true)}
                            size="large"
                            className="alerts-btn"
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                borderRadius: '6px',
                                fontWeight: 600,
                                background: allAlerts.length > 0 ? '#ff4d4f' : '#fff',
                                borderColor: allAlerts.length > 0 ? '#ff4d4f' : '#d9d9d9',
                                color: allAlerts.length > 0 ? '#fff' : 'rgba(0,0,0,0.85)'
                            }}
                        >
                            Alerts {allAlerts.length > 0 && <span style={{ marginLeft: 8, background: 'white', color: '#ff4d4f', padding: '0 6px', borderRadius: '4px', fontSize: '12px' }}>{allAlerts.length}</span>}
                        </Button>
                        <Button
                            icon={<FileTextOutlined />}
                            onClick={() => setIsProformaVisible(true)}
                            size="large"
                        >
                            Generate Proforma
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                            size="large"
                        >
                            New Order
                        </Button>
                    </Space>
                }
            >
                <div style={{ marginBottom: 20 }}>
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} md={8}>
                            <Search
                                placeholder="Search by ID, customer name or reference..."
                                allowClear
                                enterButton={<SearchOutlined />}
                                onSearch={handleSearch}
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col xs={24} md={16}>
                            <Space wrap split={<Divider type="vertical" />}>
                                <div>
                                    <Text type="secondary" style={{ marginRight: 8, fontSize: 12 }}>STATUS</Text>
                                    <Select
                                        placeholder="All Status"
                                        style={{ width: 140 }}
                                        allowClear
                                        onChange={(val) => handleFilterChange('status', val)}
                                        value={filters.status || undefined}
                                    >
                                        <Option value="Open">Open</Option>
                                        <Option value="InProgress">In Progress</Option>
                                        <Option value="Completed">Completed</Option>
                                        <Option value="Closed">Closed</Option>
                                    </Select>
                                </div>
                                <div>
                                    <Text type="secondary" style={{ marginRight: 8, fontSize: 12 }}>DISTRICT</Text>
                                    <Input
                                        placeholder="Search District"
                                        style={{ width: 140 }}
                                        allowClear
                                        onChange={(e) => handleFilterChange('district', e.target.value)}
                                        value={filters.district}
                                    />
                                </div>
                                <div>
                                    <Text type="secondary" style={{ marginRight: 8, fontSize: 12 }}>PLANT SIZE</Text>
                                    <InputNumber
                                        placeholder="kW"
                                        style={{ width: 80 }}
                                        onChange={(val) => handleFilterChange('kw', val)}
                                        value={filters.kw}
                                    />
                                </div>
                            </Space>
                        </Col>
                    </Row>
                </div>
                <Table
                    dataSource={orders}
                    loading={loading}
                    rowKey="id"
                    onChange={handleTableChange}
                    pagination={pagination}
                    scroll={{ x: 1200 }}
                >
                    <Column
                        title="Order ID"
                        dataIndex="id"
                        key="id"
                        width={100}
                        render={(id) => `#${id}`}
                    />
                    <Column
                        title="Customer"
                        dataIndex={['customer', 'customerName']}
                        key="customer"
                        width={250}
                        render={(text, record) => (
                            <Space>
                                <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#87d068' }} />
                                <div>
                                    <div style={{ fontWeight: 600 }}>{text}</div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>{record.customer?.customerMobile}</div>
                                </div>
                            </Space>
                        )}
                    />
                    <Column
                        title="District"
                        dataIndex={['customer', 'district']}
                        key="district"
                        width={150}
                        render={(district) => district || <Text type="secondary">N/A</Text>}
                    />
                    <Column
                        title="Date"
                        dataIndex="orderDate"
                        key="orderDate"
                        render={(date) => new Date(date).toLocaleDateString()}
                    />
                    <Column
                        title="Plant Size"
                        dataIndex="plantSizeKw"
                        key="plantSizeKw"
                        render={(size) => `${size || 0} Kw`}
                    />
                    <Column
                        title="Status"
                        dataIndex="projectStatus"
                        key="projectStatus"
                        render={(status) => (
                            <Tag color={statusColors[status] || 'default'}>
                                {status}
                            </Tag>
                        )}
                    />
                    <Column
                        title="Balance"
                        key="balance"
                        render={(_, record) => {
                            const totalPaid = (record.milestones || []).reduce((sum, m) => sum + (m.amount || 0), 0);
                            const balance = (record.orderValue || 0) - totalPaid;
                            return (
                                <Text strong style={{ color: balance > 0 ? '#ff4d4f' : '#52c41a' }}>
                                    ₹{balance.toLocaleString()}
                                </Text>
                            );
                        }}
                    />
                    <Column
                        title="Value"
                        dataIndex="orderValue"
                        key="orderValue"
                        render={(value) => `₹${(value || 0).toLocaleString()}`}
                    />
                    <Column
                        title="Actions"
                        key="actions"
                        fixed="right"
                        render={(record) => (
                            <Space>
                                <Tooltip title="View Details">
                                    <Button
                                        type="text"
                                        icon={<EyeOutlined style={{ color: '#52c41a' }} />}
                                        onClick={() => navigate(`/orders/${record.id}`)}
                                    />
                                </Tooltip>
                                <Tooltip title="Edit">
                                    <Button
                                        type="text"
                                        icon={<EditOutlined style={{ color: '#1890ff' }} />}
                                        onClick={() => handleEdit(record)}
                                    />
                                </Tooltip>
                                <Popconfirm
                                    title="Delete Order"
                                    description="Are you sure you want to delete this order?"
                                    onConfirm={() => handleDelete(record.id)}
                                    okText="Yes"
                                    cancelText="No"
                                    okButtonProps={{ danger: true }}
                                >
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                    />
                                </Popconfirm>
                            </Space>
                        )}
                    />
                </Table>
            </Card>

            <Drawer
                title={selectedOrder ? 'Edit Order' : 'New Order'}
                width={1100}
                onClose={() => {
                    setIsDrawerVisible(false);
                    setSearchParams({});
                }}
                open={isDrawerVisible}
                destroyOnClose
                maskClosable={false}
            >
                <OrderForm
                    order={selectedOrder}
                    onSuccess={() => {
                        setIsDrawerVisible(false);
                        setSearchParams({});
                        fetchOrders(pagination.current, searchText);
                    }}
                    onCancel={() => {
                        setIsDrawerVisible(false);
                        setSearchParams({});
                    }}
                />
            </Drawer>

            <Drawer
                title={
                    <Space>
                        <BellOutlined style={{ color: '#ff4d4f' }}/>
                        <span>Delayed Orders Tracking</span>
                    </Space>
                }
                width={500}
                onClose={() => setIsAlertsDrawerVisible(false)}
                open={isAlertsDrawerVisible}
                destroyOnClose
            >
                {allAlerts.length === 0 ? (
                    <div style={{ textAlign: 'center', marginTop: 100 }}>
                        <Text type="secondary">All order milestones are on track.</Text>
                    </div>
                ) : (
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                        {allAlerts.map((alert, index) => (
                            <Card 
                                key={index} 
                                size="small" 
                                style={{ 
                                    borderRadius: '8px',
                                    border: '1px solid #ffccc7',
                                    background: '#fff1f0',
                                    marginBottom: '8px'
                                }}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Tag color="error" style={{ borderRadius: '3px', fontWeight: 600, margin: 0 }}>{alert.milestone}</Tag>
                                            <span style={{ fontSize: '11px', color: '#8c8c8c' }}>Order #{alert.orderId}</span>
                                        </div>
                                        {alert.daysDelayed > 0 && (
                                            <Tag color="error" bordered={false} style={{ fontSize: '10px', height: '18px', lineHeight: '18px', margin: 0, fontWeight: 700, background: '#fff2f0', color: '#ff4d4f' }}>
                                                {alert.daysDelayed}d DELAYED
                                            </Tag>
                                        )}
                                    </div>

                                    <div style={{ padding: '0 2px' }}>
                                        <div style={{ fontSize: '16px', fontWeight: 800, color: 'rgba(0,0,0,0.85)', lineHeight: 1.2 }}>
                                            {alert.customer}
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#ff4d4f', fontWeight: 500, marginTop: '4px', opacity: 0.9 }}>
                                            {alert.message}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2px' }}>
                                        <Button 
                                            type="primary"
                                            danger
                                            size="small"
                                            onClick={() => {
                                                navigate(`/orders/${alert.orderId}`);
                                                setIsAlertsDrawerVisible(false);
                                            }}
                                            style={{ 
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                height: '28px',
                                                fontWeight: 600,
                                                padding: '0 16px',
                                                boxShadow: '0 2px 4px rgba(255, 77, 79, 0.2)'
                                            }}
                                        >
                                            Track Order
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </Space>
                )}
            </Drawer>

            <ProformaGenerator
                visible={isProformaVisible}
                onCancel={() => setIsProformaVisible(false)}
                orders={orders}
                customers={customers}
            />
        </Space>
    );
};

export default OrderList;